class CartDrawer extends HTMLElement {
	constructor() {
		super();
		this._onScrollOrResize = this.pinToStickyHeader.bind(this);
		this._onDrawerClick = this.onDrawerClick.bind(this);
	}

	connectedCallback() {
		if (this._initialized) return;
		this._initialized = true;

		this.sectionHeader = document.querySelector(".shopify-section-header");
		this.header = document.querySelector(".header-wrapper");
		this.headerOverlay = document.querySelector(".header--has-overlay");

		if (this.header) this.header.preventHide = false;

		this.addEventListener(
			"keyup",
			(evt) => evt.code === "Escape" && this.close()
		);
		// Delegate clicks so handlers survive #CartDrawer innerHTML re-renders.
		this.addEventListener("click", this._onDrawerClick);
		this.setHeaderCartIconAccessibility();

		const headerAccount = document.querySelector(".header__account");
		if (headerAccount) {
			headerAccount.addEventListener("click", () => {
				if (this.classList.contains("active")) {
					this.close();
				}
			});
		}

		const localization = document.querySelectorAll(".header__localization");
		localization.forEach((localization) => {
			localization.addEventListener("click", () => {
				if (
					localization.querySelector("button[aria-expanded=true]") &&
					this.classList.contains("active")
				) {
					this.close();
				}
			});
		});
	}

	onDrawerClick(event) {
		if (event.target.closest("#CartDrawer-Overlay")) {
			this.close();
			return;
		}

		if (!event.target.closest(".cart__note-close-button")) return;

		const noteText = document.getElementById("CartDrawer-Note");
		const noteEdit = document.getElementById("CartDrawer-Note-Edit");
		if (noteEdit && noteText) {
			if (noteText.value.trim() !== "") {
				noteEdit.classList.add("show");
			} else {
				noteEdit.classList.remove("show");
			}
		}
		document.getElementById("Details-CartDrawer")?.removeAttribute("open");
	}

	setHeaderPreventHide(enabled) {
		const header =
			this.header ||
			document.querySelector("sticky-header") ||
			document.querySelector(".header-wrapper");
		this.header = header;
		if (header) header.preventHide = Boolean(enabled);
	}

	pinToStickyHeader() {
		if (!this.classList.contains("active")) return;

		// Keep header visible while drawer is open. preventHide skips hide(), but
		// also skips applying sticky on scroll-down — so we pin it ourselves.
		this.setHeaderPreventHide(true);
		const headerSection =
			this.sectionHeader || document.querySelector(".shopify-section-header");
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

		if (headerSection) {
			headerSection.classList.remove("shopify-section-header-hidden");
			if (scrollTop > 0) {
				headerSection.classList.add("shopify-section-header-sticky");
			}
		}

		const isDesktop = window.matchMedia("(min-width: 750px)").matches;

		if (!isDesktop) {
			this.style.top = "0px";
			this.style.height = "100dvh";
			return;
		}

		if (!headerSection) {
			this.style.top = "";
			this.style.height = "";
			return;
		}

		// Follow sticky header: below announcement at top, flush under header when stuck.
		const headerBottom = Math.max(
			0,
			Math.round(headerSection.getBoundingClientRect().bottom)
		);
		this.style.top = `${headerBottom}px`;
		this.style.height = `calc(100dvh - ${headerBottom}px)`;
	}

	setHeaderCartIconAccessibility() {
		const cartLink = document.querySelector("#cart-icon-bubble");
		if (!cartLink) return;
		cartLink.setAttribute("role", "button");
		cartLink.setAttribute("aria-haspopup", "dialog");
		cartLink.addEventListener("click", (event) => {
			event.preventDefault();
			if (!this.classList.contains("active")) this.open(cartLink);
			else this.close();
		});
		cartLink.addEventListener("keydown", (event) => {
			if (event.code.toUpperCase() === "SPACE") {
				event.preventDefault();
				if (!this.classList.contains("active")) this.open(cartLink);
				else this.close();
			}
		});
	}

	open(triggeredBy) {
		this.setHeaderPreventHide(true);
		if (triggeredBy) this.setActiveElement(triggeredBy);
		const cartDrawerNote = this.querySelector('[id^="Details-"] summary');

		if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
			this.setSummaryAccessibility(cartDrawerNote);

		// Keep header visible while open; do not lock body scroll (breaks sticky).
		this.sectionHeader?.classList.remove("shopify-section-header-hidden");

		setTimeout(() => {
			this.classList.add("animate", "active");
			this.pinToStickyHeader();
			// Remove before add so re-open (e.g. cart AJAX) does not stack listeners.
			window.removeEventListener("scroll", this._onScrollOrResize);
			window.removeEventListener("resize", this._onScrollOrResize);
			window.addEventListener("scroll", this._onScrollOrResize, {
				passive: true,
			});
			window.addEventListener("resize", this._onScrollOrResize);
		});

		this.addEventListener(
			"transitionend",
			() => {
				const containerToTrapFocusOn = this.classList.contains("is-empty")
					? this.querySelector(".drawer__inner-empty")
					: document.getElementById("CartDrawer");
				const focusElement =
					this.querySelector(".drawer__inner") ||
					this.querySelector(".drawer__close");
				trapFocus(containerToTrapFocusOn, focusElement);
				this.pinToStickyHeader();
			},
			{ once: true }
		);

		this.headerOverlay?.classList.remove("color-background-overlay");
		this.headerOverlay?.classList.add("color-background-overlay-hidden");
	}

	close() {
		this.classList.remove("active");
		window.removeEventListener("scroll", this._onScrollOrResize);
		window.removeEventListener("resize", this._onScrollOrResize);
		this.style.top = "";
		this.style.height = "";
		removeTrapFocus(this.activeElement);
		this.setHeaderPreventHide(false);

		this.headerOverlay?.classList.add("color-background-overlay");
		this.headerOverlay?.classList.remove("color-background-overlay-hidden");
	}

	setSummaryAccessibility(cartDrawerNote) {
		cartDrawerNote.setAttribute("role", "button");
		cartDrawerNote.setAttribute("aria-expanded", "false");

		if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
			cartDrawerNote.setAttribute(
				"aria-controls",
				cartDrawerNote.nextElementSibling.id
			);
		}

		cartDrawerNote.addEventListener("click", (event) => {
			event.currentTarget.setAttribute(
				"aria-expanded",
				!event.currentTarget.closest("details").hasAttribute("open")
			);
		});

		cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
	}

	async renderContents(parsedState) {
		this.querySelector(".drawer__inner").classList.contains("is-empty") &&
			this.querySelector(".drawer__inner").classList.remove("is-empty");
		this.productId = parsedState.id;
		const sections =
			parsedState?.sections || (await this.fetchSectionsFallback());
		if (!sections) return;
		this.getSectionsToRender().forEach((section) => {
			const sectionElement = section.selector
				? document.querySelector(section.selector)
				: document.getElementById(section.id);
			if (!sectionElement || !sections[section.id]) return;
			sectionElement.innerHTML = this.getSectionInnerHTML(
				sections[section.id],
				section.selector
			);
		});

		setTimeout(() => {
			this.open();
		});
	}

	getSectionInnerHTML(html, selector = ".shopify-section") {
		const parsedDocument = new DOMParser().parseFromString(html, "text/html");
		const element = selector ? parsedDocument.querySelector(selector) : null;
		return element ? element.innerHTML : html;
	}

	async fetchSectionsFallback() {
		const sectionIds = this.getSectionsToRender().map((section) => section.id);
		const response = await fetch(
			`${routes.cart_url}?sections=${sectionIds.join(",")}`,
			{
				headers: { Accept: "application/json" },
			}
		);
		if (!response.ok) return null;
		return response.json();
	}

	getSectionsToRender() {
		return [
			{
				id: "cart-drawer",
				selector: "#CartDrawer",
			},
			{
				id: "cart-icon-bubble",
			},
		];
	}

	getSectionDOM(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector);
	}

	setActiveElement(element) {
		this.activeElement = element;
	}
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
	getSectionsToRender() {
		return [
			{
				id: "CartDrawer",
				section: "cart-drawer",
				selector: ".drawer__inner",
			},
			{
				id: "cart-icon-bubble",
				section: "cart-icon-bubble",
				selector: ".shopify-section",
			},
		];
	}
}

customElements.define("cart-drawer-items", CartDrawerItems);

// ATC for care products
document.addEventListener('DOMContentLoaded', function() {
    const MAX_CART_LIMIT = window.checkout_limit || 10;

    window.validateCartLimit = async function() {
		try {
			const cartUrl = (window.Shopify && window.Shopify.routes)
							? `${window.Shopify.routes.root}cart.js`
							: '/cart.js';

			const response = await fetch(cartUrl, {
				method: 'GET',
				headers: {
					'Accept': 'application/json',
					'Cache-Control': 'no-cache'
				}
			});

			if (!response.ok) return;

			const cart = await response.json();
			const currentCount = cart.item_count;

			document.querySelectorAll('.ajax-submit').forEach(form => {
				const submitButton = form.querySelector('[type="submit"]');
				if (!submitButton) return;

				if (currentCount >= (window.checkout_limit || 10)) {
					submitButton.classList.add('disabled', 'button--error');
					submitButton.disabled = true;
				} else {
					submitButton.classList.remove('disabled', 'button--error');
					submitButton.disabled = false;
				}
			});
		} catch (e) {
			console.warn('Cart check skipped');
		}
	};

    window.validateCartLimit();

    document.addEventListener('click', (event) => {
        if (event.target.closest('cart-drawer') || event.target.closest('.cart-remove-button')) {
            setTimeout(() => {
                window.validateCartLimit();
            }, 600);
        }
    });

    document.querySelectorAll('.ajax-submit').forEach(form => {
        form.addEventListener('submit', function(e) {
            e.preventDefault();
            const submitButton = this.querySelector('[type="submit"]');
            if (submitButton.disabled) return;

            const formData = new FormData(this);
            formData.append('sections', 'cart-drawer,cart-icon-bubble');

            fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                body: formData,
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
            })
            .then(response => response.json())
            .then(data => {
                if (data.status) throw new Error(data.description || data.message);
                
                const cartDrawer = document.querySelector('cart-drawer');
                if (cartDrawer && cartDrawer.renderContents) {
                    cartDrawer.renderContents(data);
                }
                window.validateCartLimit();
            })
            .catch(() => {
                window.validateCartLimit();
            });
        });
    });
});

// ATC for gifted products
document.addEventListener('DOMContentLoaded', function() {
  document.querySelectorAll('.ajax-submit-gift').forEach(button => {
    button.addEventListener('click', function(e) {
      e.preventDefault();
      
      const cartDrawer = document.querySelector('cart-drawer');
      const variantId = this.getAttribute('data-variant-id');
      
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      
      formData.append('sections', 'cart-drawer,cart-icon-bubble');
      formData.append('sections_url', '/');

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData
      })
      .then(response => response.json())
      .then(parsedState => {
        if (parsedState.status) {
          console.error('Fehler:', parsedState.description);
          return;
        }
        
        if (cartDrawer) {
          cartDrawer.renderContents(parsedState);
        }
      })
      .catch(error => {
        console.error('Error:', error);
      });
    });
  });
});
